import { ElementAttribute } from "../../../../library-js/dom-tools/ElementAttribute";
import { ElementType } from "../../../../library-js/dom-tools/ElementType";
import { HTMLEditor } from "../../../../library-js/dom-tools/HTMLEditor";
import { ISOTimeStamp } from "../../../../library/ts/vanilla/time/ISOTimeStamp";
import { LocalIO } from "../../../js/_internal/LocalIO";
import { StreamerDB } from "./StreamerDB";
import { HTMLNodeTreeWalker } from "../../../../library-js/graphs/HTMLNodeTreeWalker";
import { RegExpUtility } from "../../../../library-js/text/RegExpUtility";
import { AttributeValue } from "../../../../library-js/dom-tools/AttributeValue";
import { OnChange, OnClick } from "../../../../library-js/dom-tools/EventListeners";
import { Arrays } from "../../../../library-js/tools/Arrays";

export class StreamerEntry
{
  name:string;
  twitchHandle:string;
  image:string;
  followers:number;
  viewers:number;
  views:number;
  viewersFollowersRatio:number;
  rank:number;
  exclude:boolean;
  emailValidated:boolean;
  emailCandidates:string[];
  emailCheckTime:ISOTimeStamp;

  static createCSVHeader():string[]
  {
    let header = 
    [
      "Name", "Email", "Language", "Email Type", 
      "Twitch", "Twitch Followers", 
      "YouTube", "YouTube Followers", 
      "Kick", "Kick Followers", 
      "Comments", 
    ];

    return header;
  }

  static getCSVLine( s:StreamerEntry ):string[]
  {
    let line = 
    [
      s.name, s.emailCandidates[ 0 ], "de", "Dash Cup Kickers Streamer Invite",
      `https://www.twitch.tv/${s.twitchHandle}/about`, s.followers + "",
      "","",
      "","",
      ""
    ];

    return line;
  }

  static createClone( original: StreamerEntry )
  {
    let se = new StreamerEntry();

    se.name = original.name;
    se.twitchHandle = original.twitchHandle;
    se.image = original.image;
    se.exclude = original.exclude;
    se.followers = original.followers;
    se.viewers = original.viewers;
    se.views = original.views;
    se.viewersFollowersRatio = original.viewersFollowersRatio;
    se.rank = original.rank;
    se.emailValidated = original.emailValidated || false ;
    se.emailCandidates = original.emailCandidates || [];
    se.emailCheckTime = original.emailCheckTime || null;
    

    return se;
  }

  static getFilteredEmailCandidates( candidates:string[] ):string[]
  {
    if ( ! candidates || candidates.length === 0 )
    {
      return [];
    }


    let filtered = new Set<string>();

    let isYouTubeLink = /youtube\.com\/@/;
    let isTikTokLink  = /tiktok\.com\/@/; 

    candidates.forEach(
      ( c )=>
      {
        if ( filtered.has( c ) || isYouTubeLink.test( c ) || isTikTokLink.test( c ) )
        {
          return;
        }

        filtered.add( c );
      }
    )

    


    return [ ... filtered ];
  }


  static $( e:string|Element ):HTMLElement
  {
    return StreamerDB.streamerValue.create( e ) as HTMLElement;
  }
 

  static safeViews( e:StreamerEntry)
  {
    return e.views || 0;
  }

  static _numberInfo( value:number )
  {
    if ( value === null || value === undefined )
    {
      return "--";
    }

    if ( value >= 1000000 )
    {
      return ( value / 1000000 ).toFixed( 2 ) + "M"; 
    }

    if ( value >= 1000)
    {
      return ( value / 1000 ).toFixed( 1 ) + "K"; 
    }

    return value;
  }

  static getEmailRegex()
  {
    let adressor = /(?:\w|\.|\!|\#|\$|\%|\&|\'|\*|\+|\-|\/|\=|\?|\^|\_|\`|\{|\||\}|\~)+/;
    let atRegex = /@(?:\w|\-|\_)+.(?:\w|\-|\_)+(?:\.(?:\w|\-|\_)+)?/;
  
    return new RegExp( adressor.source + atRegex.source, "g" )
  }

  static getEmailFromSource( source:string )
  {
    let doc = HTMLEditor.stringToDocument( source );

    let walker = HTMLNodeTreeWalker.$;

    let candidates:string[] = [];

    walker.forAll( doc.documentElement, 
      ( n )=>
      {
        if ( n.nodeType != Node.TEXT_NODE )
        {
          return;
        }

        let emailRegex = this.getEmailRegex();

        let textContent = n.textContent;
        let matchResult = emailRegex.exec( textContent  );

        while ( matchResult )
        {
          candidates.push( matchResult[ 0 ] );
          matchResult = emailRegex.exec( textContent )
        }
      }
    );

    return candidates;
  }

  static async scanForEmail( se:StreamerEntry ):Promise<string[]>
  {
    let cachePath = `${StreamerDB.cacheDirectoryPath}${se.twitchHandle}.html`;

    let exists = await LocalIO.exists( cachePath );

    if ( exists )
    {
      console.log( "from cache:", se.twitchHandle );
      let result = await LocalIO.loadText( cachePath );
      return StreamerEntry.getEmailFromSource( result );
    }
    

    console.log( "scanning:", se.twitchHandle );
    let url = StreamerEntry.getTwitchAboutHandle( se.twitchHandle );
    let page = await LocalIO.getHtmlPage( url );
    console.log( "storing:", cachePath );

    await LocalIO.saveText( cachePath, page );

    return StreamerEntry.getEmailFromSource( page );
    

  }

  static getTwitchAboutHandle( twitchHandle:string ):string
  {
    return `https://www.twitch.tv/${twitchHandle}/about`;
  }

  static createHTML( se:StreamerEntry, list:Element, updateList:()=>Promise<void>, saveData:()=>Promise<void> )
  {
    let SSE = StreamerEntry;
    let anchor = ElementType.anchor.create( ElementType.b.create( se.name ) );

    ElementAttribute.href.to( anchor, `https://www.twitch.tv/${se.twitchHandle}/about` );

    let vfr = se.viewersFollowersRatio || 0;


    let emailInfo = "---";
    let emails = StreamerEntry.getFilteredEmailCandidates( se.emailCandidates );

    if ( emails.length > 0 )
    {
      emailInfo = emails.join( ", " );
    }

    let checkBox = ElementType.input.create();
    AttributeValue.type_checkbox.set( checkBox );
    checkBox.checked = se.exclude;

    let nsfwContainer = new ElementType( "exclude-info" ).create(
      [
        " Exclude:", checkBox,
      ]
    );

    OnChange.add( checkBox, 
      ()=>
      { 
        se.exclude = checkBox.checked;
        console.log( "saving Data:", se.exclude );
        saveData();
      }
    );


    let emailCheckBox = ElementType.input.create();
    AttributeValue.type_checkbox.set( emailCheckBox );
    emailCheckBox.checked = se.emailValidated;


    let emailValidContainer = new ElementType( "email-valid-info" ).create(
      [
        " Email:", emailCheckBox,
      ]
    );

    OnChange.add( emailCheckBox, 
      ()=>
      { 
        se.emailValidated = emailCheckBox.checked;
        console.log( "saving Data:", se.emailValidated );
        saveData();
      
      }
    );

    
    let emailButtons = emails.map(
      ( em )=>
      {
        let emailCandidate = new ElementType<HTMLElement>( "email-candidate" ).create( em );

        OnClick.add( emailCandidate,
          async ( e ) =>
          {
            console.log( "cnd:", se.emailCandidates.join( "," ) )
            Arrays.moveToFront( se.emailCandidates, em );

            console.log( "cnd:", se.emailCandidates.join( "," ) )
            await updateList();
            await saveData();
          }
        );

        return emailCandidate;
      }
    );

    let emailsChildren:(string|HTMLElement)[] = [ " 📧 [ " ];
    emailsChildren = emailsChildren.concat( emailButtons );
    emailsChildren = emailsChildren.concat( [ " ]" ] );
    
    let emailsContainer = new ElementType( "email-info" ).create( emailsChildren );
    

    let kids = 
    [
      anchor, 
      nsfwContainer,
      emailValidContainer,
      emailsContainer,
      " #" + se.rank + "",
      " F:" + SSE._numberInfo( se.followers ),
      " A:" + SSE._numberInfo( se.viewers ),
      " A/F:" +  vfr.toFixed( 2 ) + "%",
      " V:" + SSE._numberInfo( se.views )         
    ]

    let sortNames = 
    [     
      "Name",
      "NSFW",
      "Email Valid",
      "Email",
      "Rank",
      "Followers",
      "Viewers",
      "Viewers-Followers-Ratio",
      "Views"
    ]

    let values = kids.map( k => SSE.$( k ) );

    for ( let i = 0; i < sortNames.length; i++ )
    {
      StreamerDB.sorting.to( values[ i ], sortNames[ i ] );
    }

    let icon = ElementType.image.create();
    ElementAttribute.src.to( icon, se.image );

    values = [ icon as HTMLElement ].concat( values );

    let entry = StreamerDB.streamerEntry.create( values ) as HTMLElement;



    list.appendChild( entry );
  }
}

export class PageStreamerEntry extends StreamerEntry
{
  element:Element;
  path:string;

  

  static create( e:Element, path:string )
  {
    let entry = new PageStreamerEntry();
    entry.path = path;
    
    entry.element = e;

    entry._loadFromElement();     

    return entry;
  }

  

  _loadFromElement()
  {
    let hrefSelector = 'td a[href^="https://twitchtracker.com/"]';
    let hrefs = this.element.querySelectorAll( hrefSelector );
    this.name = hrefs[ 1 ].textContent;
    this.twitchHandle = ElementAttribute.href.from( hrefs[ 1 ] ).replace( "https://twitchtracker.com/", "" );

    let imageSource = ElementAttribute.src.from( ElementType.image.query( this.element ) );
    imageSource = decodeURIComponent( imageSource );
    this.image = LocalIO.createLoadingPath( `${this.path}${imageSource}` );

    this.rank = parseInt( this.element.querySelector( "td" ).textContent.replace( "#", "" ) );
    this.followers = StreamerDB.parseToNumber( this.element.querySelectorAll( ".color-followers" )[ 1 ].textContent );
    this.viewers   = StreamerDB.parseToNumber( this.element.querySelectorAll( ".color-viewers" )[ 0 ].textContent );
    this.views     = StreamerDB.parseToNumber( this.element.querySelectorAll( ".color-views" )[ 0 ].textContent );
    this.viewersFollowersRatio = this.viewers / this.followers * 100;
  }
  
}